import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plane, ArrowLeftRight, Users, Calendar, Search as SearchIcon,
  ChevronDown, MapPin, Loader2, ExternalLink, Ticket, Check,
} from "lucide-react";
import { api } from "@/lib/api";
import { useLocation } from "@/context/LocationContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import FindPayCheckout from "@/components/FindPayCheckout";
import PlacePicker from "@/components/PlacePicker";

/**
 * Flights — Google-Flights-style dedicated search experience.
 *
 * Slice: replaces the generic `/search?category=flight` listing view with a
 * real trip-planner. From/To free-text, round-trip toggle, passengers, cabin
 * class, departure + return dates, then a search that hits our own mock
 * `/api/travel/search?mode=flight` adapter. Each result exposes BOTH
 *   · a deep-link to partner OTAs (MakeMyTrip, Skyscanner, Goibibo, Ixigo,
 *     EaseMyTrip) — matches Google-Flights behaviour
 *   · an in-app "Book" button that calls `/api/travel/book` (mock confirm).
 */
const TRIP_TYPES = [
  { key: "round",    label: "Round trip" },
  { key: "oneway",   label: "One-way" },
  { key: "multi",    label: "Multi-city" },
];
const CABINS = ["Economy", "Premium Economy", "Business", "First"];

const OTA_PARTNERS = [
  { name: "MakeMyTrip",  url: "https://www.makemytrip.com/flights/",              bg: "#eb2226" },
  { name: "Skyscanner",  url: "https://www.skyscanner.co.in/transport/flights/", bg: "#0770e3" },
  { name: "Goibibo",     url: "https://www.goibibo.com/flights/",                bg: "#ff4b4b" },
  { name: "Ixigo",       url: "https://www.ixigo.com/flights/",                  bg: "#f2851f" },
  { name: "EaseMyTrip",  url: "https://www.easemytrip.com/flights.html",         bg: "#008cff" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const plusDaysISO = (n) => {
  const d = new Date(); d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const rupees = (paise) => `₹${Math.round((paise || 0) / 100).toLocaleString("en-IN")}`;
const durationLabel = (min) => `${Math.floor(min/60)}h ${min%60}m`;

export default function Flights() {
  const { loc } = useLocation() || {};
  const { user } = useAuth() || {};

  const [tripType, setTripType]     = useState("round");
  const [passengers, setPassengers] = useState(1);
  const [cabin, setCabin]           = useState("Economy");
  // Structured place: { city, iata?, lat, lng, country_code, kind }
  const [from, setFrom] = useState(
    loc?.city ? { city: loc.city, iata: null, lat: loc.lat, lng: loc.lng, kind: "city" } : null
  );
  const [to, setTo]     = useState(null);
  const [depart, setDepart]         = useState(plusDaysISO(3));
  const [ret, setRet]               = useState(plusDaysISO(10));

  const [loading, setLoading]       = useState(false);
  const [results, setResults]       = useState(null);   // null · [] · [{...}]
  const [booking, setBooking]       = useState(null);   // ref currently being booked
  const [booked, setBooked]         = useState({});     // {ref: booking_id}
  const [fpRow, setFpRow]           = useState(null);   // row currently paying via FindPay

  const canSearch = useMemo(
    () => !!(from?.city) && !!(to?.city) && depart && (tripType !== "round" || ret),
    [from, to, depart, ret, tripType]
  );

  const swap = () => { const t = from; setFrom(to); setTo(t); };

  // Helper: display a place as "City · IATA" for the results header + booking
  const placeLabel = (p) => p ? (p.iata ? `${p.city} (${p.iata})` : p.city) : "";

  const search = async () => {
    if (!canSearch) return;
    setLoading(true); setResults(null);
    try {
      const r = await api.get("/travel/search", {
        params: {
          mode: "flight",
          from_place: placeLabel(from),
          to_place:   placeLabel(to),
          date: depart,
        },
      });
      setResults(r.data?.results || []);
    } catch (e) {
      toast.error("Could not fetch flights. Try again.");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const bookNow = async (row) => {
    if (!user) { toast.error("Please sign in to book."); return; }
    // Open the FindPay drawer first; on payment success we call /travel/book.
    setFpRow(row);
  };

  const finalizeFlight = async (row, txn) => {
    setBooking(row.ref);
    try {
      const r = await api.post("/travel/book", {
        mode: "flight",
        from_place: placeLabel(from),
        to_place:   placeLabel(to),
        check_in: depart,
        check_out: tripType === "round" ? ret : null,
        passengers,
        provider_ref: row.ref,
        findpay_txn_id: txn?.txn_id || null,
      });
      setBooked((b) => ({ ...b, [row.ref]: r.data?.booking_id }));
      toast.success(`Booked ${row.operator} · ${r.data?.booking_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    } finally {
      setBooking(null); setFpRow(null);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="flights-page">
      {/* Hero header */}
      <div className="bg-gradient-to-b from-sky-50 to-white border-b border-sky-100">
        <div className="max-w-5xl mx-auto px-4 pt-8 pb-4 sm:pt-12 sm:pb-6">
          <div className="flex items-center gap-2 text-xs text-stone-500 mb-3">
            <Link to="/" className="hover:text-stone-700">Home</Link>
            <span>·</span>
            <span className="text-stone-700 font-medium">Flights</span>
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900 tracking-tight text-center"
            data-testid="flights-title"
          >
            Flights
          </h1>
          <p className="text-center text-stone-500 mt-2 text-sm">
            Compare fares across airlines & OTAs — deep-link to book or confirm in-app.
          </p>
        </div>
      </div>

      {/* Search card */}
      <div className="max-w-5xl mx-auto px-4 -mt-2">
        <div
          className="bg-white border border-stone-200 rounded-2xl shadow-sm p-4 sm:p-6"
          data-testid="flights-search-card"
        >
          {/* Row 1: trip type · passengers · cabin */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <SelectPill
              icon={<ArrowLeftRight size={16} />}
              value={tripType}
              onChange={setTripType}
              options={TRIP_TYPES.map(t => ({ value: t.key, label: t.label }))}
              testid="trip-type"
            />
            <SelectPill
              icon={<Users size={16} />}
              value={String(passengers)}
              onChange={(v) => setPassengers(Number(v))}
              options={Array.from({ length: 9 }, (_, i) => ({ value: String(i + 1), label: `${i + 1}` }))}
              testid="passengers"
            />
            <SelectPill
              value={cabin}
              onChange={setCabin}
              options={CABINS.map(c => ({ value: c, label: c }))}
              testid="cabin"
            />
          </div>

          {/* Row 2: From ↔ To (airport / IATA autocomplete) */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-center gap-3">
            <PlacePicker
              value={from}
              onChange={setFrom}
              placeholder="Where from?"
              icon={<span className="inline-block w-3 h-3 rounded-full border-2 border-stone-400" />}
              testid="from-picker"
            />
            <button
              type="button"
              onClick={swap}
              className="mx-auto sm:mx-0 w-10 h-10 rounded-full border border-stone-300 bg-white hover:bg-stone-50 flex items-center justify-center text-stone-600"
              aria-label="Swap origin and destination"
              data-testid="swap-btn"
            >
              <ArrowLeftRight size={16} />
            </button>
            <PlacePicker
              value={to}
              onChange={setTo}
              placeholder="Where to?"
              icon={<MapPin size={16} className="text-stone-500" />}
              testid="to-picker"
            />
          </div>

          {/* Row 3: dates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <DateField
              label="Departure"
              value={depart}
              min={todayISO()}
              onChange={setDepart}
              testid="depart-date"
            />
            <DateField
              label="Return"
              value={tripType === "round" ? ret : ""}
              min={depart || todayISO()}
              onChange={setRet}
              disabled={tripType !== "round"}
              testid="return-date"
            />
          </div>

          {/* Search button */}
          <div className="mt-5 flex justify-center">
            <button
              type="button"
              onClick={search}
              disabled={!canSearch || loading}
              className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-8 py-3 shadow-sm transition"
              data-testid="search-flights-btn"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <SearchIcon size={18} />}
              {loading ? "Searching…" : "Search flights"}
            </button>
          </div>
        </div>
      </div>

      {/* OTA partners strip */}
      <div className="max-w-5xl mx-auto px-4 mt-6">
        <div className="text-xs uppercase tracking-wider text-stone-500 mb-2 font-semibold">
          Compare on partner sites
        </div>
        <div className="flex flex-wrap gap-2" data-testid="ota-partners">
          {OTA_PARTNERS.map((p) => (
            <a
              key={p.name}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white hover:border-stone-300 px-4 py-2 text-sm font-semibold text-stone-800"
              data-testid={`ota-${p.name.toLowerCase()}`}
            >
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: p.bg }} />
              {p.name}
              <ExternalLink size={12} className="text-stone-400" />
            </a>
          ))}
        </div>
      </div>

      {/* Results */}
      <div className="max-w-5xl mx-auto px-4 mt-6 pb-16">
        {results === null && !loading && (
          <div className="text-center py-10 text-stone-500 text-sm" data-testid="flights-empty-hint">
            Pick your route above and hit <b>Search flights</b> to see live options.
          </div>
        )}
        {loading && (
          <div className="text-center py-12" data-testid="flights-loading">
            <Loader2 size={28} className="animate-spin text-stone-400 mx-auto" />
            <div className="text-sm text-stone-500 mt-2">Fetching flights…</div>
          </div>
        )}
        {results !== null && !loading && results.length === 0 && (
          <div className="text-center py-12 bg-white border border-stone-200 rounded-2xl" data-testid="flights-no-results">
            <Plane size={28} className="mx-auto text-stone-300 mb-2" />
            <div className="text-stone-700 font-bold">No flights found for that route.</div>
            <div className="text-sm text-stone-500 mt-1">Try different dates or nearby cities.</div>
          </div>
        )}
        {results !== null && results.length > 0 && (
          <div className="space-y-3" data-testid="flights-results">
            <div className="flex items-center justify-between text-sm text-stone-500 mb-1">
              <span>{results.length} flights · {placeLabel(from)} → {placeLabel(to)} · {depart}</span>
              <span className="text-xs">Prices are mock demo fares</span>
            </div>
            {results.map((r) => (
              <FlightRow
                key={r.ref}
                r={r}
                from={placeLabel(from)}
                to={placeLabel(to)}
                cabin={cabin}
                passengers={passengers}
                onBook={() => bookNow(r)}
                bookingRef={booking}
                bookedId={booked[r.ref]}
              />
            ))}
          </div>
        )}
      </div>
      {fpRow && (
        <FindPayCheckout
          open={!!fpRow}
          onOpenChange={(o) => !o && setFpRow(null)}
          merchantId="fpm_platform"
          amountPaise={Math.max(1, fpRow.price_paise || 100)}
          currency="INR"
          description={`Flight · ${fpRow.operator} · ${placeLabel(from)} → ${placeLabel(to)}`}
          context={{ kind: "flight_book", provider_ref: fpRow.ref, operator: fpRow.operator }}
          onSuccess={(txn) => finalizeFlight(fpRow, txn)}
        />
      )}
    </div>
  );
}

function SelectPill({ icon, value, onChange, options, testid }) {
  return (
    <label className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white hover:border-stone-300 px-3 py-2 text-sm font-medium text-stone-800 cursor-pointer">
      {icon}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent focus:outline-none pr-6 appearance-none cursor-pointer"
        data-testid={`select-${testid}`}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <ChevronDown size={14} className="-ml-4 pointer-events-none text-stone-500" />
    </label>
  );
}

function DateField({ label, value, min, onChange, disabled, testid }) {
  return (
    <label className={`flex items-center gap-2 rounded-xl border ${disabled ? "border-stone-200 bg-stone-50" : "border-stone-300 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 bg-white"} px-3 py-3`}>
      <Calendar size={16} className="text-stone-500" />
      <div className="flex-1">
        <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">{label}</div>
        <input
          type="date"
          value={value}
          min={min}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent focus:outline-none text-stone-900"
          data-testid={testid}
        />
      </div>
    </label>
  );
}

function FlightRow({ r, from, to, cabin, passengers, onBook, bookingRef, bookedId }) {
  // Deep-link builders (partner sites)
  const q = `${from}-${to}`.toLowerCase().replace(/\s+/g, "-");
  const partners = [
    { name: "MakeMyTrip", url: "https://www.makemytrip.com/flights/",              bg: "#eb2226" },
    { name: "Skyscanner", url: `https://www.skyscanner.co.in/transport/flights/${q}/`, bg: "#0770e3" },
    { name: "Ixigo",      url: `https://www.ixigo.com/search/result/flight?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, bg: "#f2851f" },
  ];
  const isBooking = bookingRef === r.ref;
  const isBooked = Boolean(bookedId);

  return (
    <div
      className="bg-white border border-stone-200 rounded-2xl p-4 sm:p-5 hover:border-stone-300 transition"
      data-testid={`flight-row-${r.ref}`}
    >
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center">
            <Plane size={20} />
          </div>
          <div>
            <div className="font-bold text-stone-900 leading-tight" data-testid={`operator-${r.ref}`}>{r.operator}</div>
            <div className="text-xs text-stone-500">
              {from} → {to} · Depart {r.depart} · {durationLabel(r.duration_min)} · {cabin} · {passengers} pax
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xl font-extrabold text-stone-900" data-testid={`price-${r.ref}`}>{rupees(r.price_paise)}</div>
          <div className="text-[11px] text-stone-500">per passenger · incl. taxes</div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {partners.map((p) => (
          <a
            key={p.name}
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 hover:border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-700"
            data-testid={`deeplink-${p.name.toLowerCase()}-${r.ref}`}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: p.bg }} />
            Book on {p.name}
            <ExternalLink size={11} className="text-stone-400" />
          </a>
        ))}
        <button
          type="button"
          onClick={onBook}
          disabled={isBooking || isBooked}
          className={`ml-auto inline-flex items-center gap-1.5 rounded-full font-semibold px-4 py-1.5 text-xs transition ${
            isBooked
              ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
              : "bg-[#E05E36] hover:bg-[#c94e2a] text-white"
          }`}
          data-testid={`book-inapp-${r.ref}`}
        >
          {isBooking ? <Loader2 size={12} className="animate-spin" /> : isBooked ? <Check size={12} /> : <Ticket size={12} />}
          {isBooked ? `Booked · ${bookedId}` : isBooking ? "Booking…" : "Book in-app"}
        </button>
      </div>
    </div>
  );
}
